// Support tickets (Ordre 08): the business words of a ticket and the small pure rules the screens share. UI only — the server
// checks every right, every transition and every delay again.
import { hasPermission } from "./permissions";
import { formatInstant, inputToIso, nowInput } from "./time";

export const TICKET_READ_KEYS = ["tickets:create", "tickets:view", "tickets:view_all", "tickets:manage", "tickets:manage_all"];
export const TICKET_CREATE_KEYS = ["tickets:create", "tickets:manage", "tickets:manage_all"];
export const TICKET_HANDLER_KEYS = ["tickets:manage", "tickets:manage_all"];

export const STATUS_LABELS = {
  new: "Nouveau",
  in_progress: "En cours",
  waiting_customer: "En attente de la pharmacie",
  waiting_third_party: "En attente d'un tiers",
  resolved: "Résolu",
  closed: "Clos",
};
export const STATUSES = Object.keys(STATUS_LABELS);
export const OPEN_STATUSES = ["new", "in_progress", "waiting_customer", "waiting_third_party"];

export const PRIORITY_LABELS = { critical: "Critique", high: "Haute", normal: "Normale", low: "Basse" };
export const PRIORITIES = Object.keys(PRIORITY_LABELS);

export const CATEGORY_LABELS = {
  bug: "Anomalie du logiciel",
  question: "Question d'utilisation",
  training: "Besoin de formation",
  data: "Données / migration",
  equipment: "Matériel",
  access: "Accès / compte",
  billing: "Facturation",
  other: "Autre",
};

export const CAUSE_LABELS = {
  software_defect: "Défaut du logiciel",
  usage: "Utilisation",
  configuration: "Configuration",
  data: "Données",
  hardware: "Matériel",
  network: "Réseau",
  third_party: "Tiers (fournisseur, opérateur…)",
  other: "Autre",
};

// How the ticket arrived (`assist` = from a remote assistance, `internal` = noticed by the team).
export const CHANNEL_LABELS = {
  phone: "Téléphone",
  whatsapp: "WhatsApp",
  email: "E-mail",
  on_site: "Sur place",
  assist: "Assistance à distance",
  internal: "Constaté par l'équipe",
};
// How a ticket arrived, for display: the form's choices plus Jamora's « Contacter le support » (08-4, set only by the signed route).
export function channelLabel(channel) {
  return channel === "jamora_app" ? "Depuis Jamora (Contacter le support)" : CHANNEL_LABELS[channel] || channel;
}
// The channel of one recorded exchange.
export const CONTACT_CHANNEL_LABELS = { phone: "Téléphone", whatsapp: "WhatsApp", email: "E-mail", on_site: "Sur place", other: "Autre" };

export const IMPACT_LABELS = {
  blocked: "Pharmacie bloquée (ventes ou caisse impossibles)",
  degraded: "Fonction dégradée (contournement possible)",
  minor: "Gêne mineure ou question",
};
export const URGENCY_LABELS = { high: "Bloque maintenant", normal: "Normale", low: "Peut attendre" };

// D-14, the same matrix as the server (tickets/rules.py): shown live in the form, always recomputed by the server.
const PRIORITY_MATRIX = {
  blocked: { high: "critical", normal: "high", low: "high" },
  degraded: { high: "high", normal: "normal", low: "normal" },
  minor: { high: "normal", normal: "low", low: "low" },
};

export function computedPriority(impact, urgency) {
  return PRIORITY_MATRIX[impact]?.[urgency] || null;
}

export function priorityTone(priority) {
  return {
    critical: "bg-red-50 text-red-700 border-red-200",
    high: "bg-amber-50 text-amber-800 border-amber-200",
    normal: "bg-sky-50 text-sky-700 border-sky-200",
    low: "bg-slate-50 text-slate-600 border-slate-200",
  }[priority] || "bg-slate-50 text-slate-600 border-slate-200";
}

export function statusTone(status) {
  return {
    new: "bg-jamora/10 text-jamora border-jamora/20",
    in_progress: "bg-sky-50 text-sky-700 border-sky-200",
    waiting_customer: "bg-violet-50 text-violet-700 border-violet-200",
    waiting_third_party: "bg-violet-50 text-violet-700 border-violet-200",
    resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-slate-100 text-slate-600 border-slate-200",
  }[status] || "bg-slate-50 text-slate-600 border-slate-200";
}

export const DELAY_TONES = {
  ok: "text-slate-600",
  at_risk: "text-amber-700",
  breached: "text-red-700",
  met: "text-emerald-700",
  missed: "text-red-700",
};

function hoursText(seconds) {
  const minutes = Math.max(1, Math.round(Math.abs(seconds) / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} j`;
}

// One measured delay (first response or resolution) in plain words: « dans 2 h », « dépassé de 3 h », « tenu », « manqué ».
export function delayText(measure, now = new Date()) {
  if (!measure) return null;
  if (measure.state === "met") return "Tenu";
  if (measure.state === "missed") return "Manqué";
  if (measure.paused) return "En pause (attente)";
  if (!measure.due_at) return null;
  const left = (new Date(measure.due_at).getTime() - now.getTime()) / 1000;
  return left >= 0 ? `dans ${hoursText(left)}` : `dépassé de ${hoursText(left)}`;
}

// The list's « Délai » column: the most urgent measure still running.
export function listDelay(ticket, now = new Date()) {
  const measures = [ticket?.sla?.first_response, ticket?.sla?.resolution].filter((m) => m && ["ok", "at_risk", "breached"].includes(m.state));
  if (measures.length === 0) return null;
  const rank = { breached: 0, at_risk: 1, ok: 2 };
  const worst = measures.sort((a, b) => rank[a.state] - rank[b.state] || (a.due_at || "").localeCompare(b.due_at || ""))[0];
  return { state: worst.state, text: delayText(worst, now) };
}

export const QUICK_VIEWS = [
  { id: "mine", label: "Mes tickets", params: { responsible: "me", state: "open" } },
  { id: "unassigned", label: "Non assignés", params: { responsible: "none", state: "open" } },
  { id: "breached", label: "En dépassement", params: { delay: "breached" } },
  { id: "waiting", label: "En attente de la pharmacie", params: { status: "waiting_customer" } },
  // 08-4: requests received from Jamora whose CRM pharmacy is not confirmed yet — only « tous » sees them.
  { id: "to_attach", label: "À rattacher", params: { to_attach: "1" }, permission: "tickets:view_all" },
];

export function canSeeTickets(user) {
  return !!user?.features?.tickets && hasPermission(user, TICKET_READ_KEYS);
}

export function canCreateTickets(user) {
  return !!user?.features?.tickets && hasPermission(user, TICKET_CREATE_KEYS);
}

// What one may do with the status, from where the ticket is (D-11). `needs`: what the dialog asks for.
export function statusMoves(ticket) {
  if (!ticket) return [];
  const has = !!ticket.responsible_user_id;
  switch (ticket.status) {
    case "new":
      return [
        ...(has ? [{ to: "in_progress", label: "Démarrer", needs: null }] : []),
        { to: "resolved", label: "Résoudre", needs: "solution" },
        { to: "closed", label: "Clore sans suite", needs: "reason", danger: true },
      ];
    case "in_progress":
      return [
        { to: "waiting_customer", label: "Attendre la pharmacie", needs: "reason" },
        { to: "waiting_third_party", label: "Attendre un tiers", needs: "reason" },
        { to: "resolved", label: "Résoudre", needs: "solution" },
      ];
    case "waiting_customer":
    case "waiting_third_party":
      return [{ to: "in_progress", label: "Reprendre", needs: null }, { to: "resolved", label: "Résoudre", needs: "solution" }];
    case "resolved":
      return [{ to: "closed", label: "Clore", needs: null }, { to: "in_progress", label: "Rouvrir", needs: "reason" }];
    case "closed":
      return [{ to: "in_progress", label: "Rouvrir", needs: "reason" }];
    default:
      return [];
  }
}

// The one button the detail page puts forward (D-72); the others go in the « … » menu.
export function primaryAction(ticket) {
  const caps = ticket?.capabilities || {};
  const done = ticket?.status === "resolved" || ticket?.status === "closed";
  if (caps.take_charge && !(done && caps.manage)) return { kind: "take_charge", label: "Prendre en charge" }; // a resolved ticket: « Rouvrir » first
  if (!caps.manage) return caps.note ? { kind: "note", label: "Ajouter une note" } : null;
  if (ticket.status === "new" && ticket.responsible_user_id) return { kind: "status", to: "in_progress", label: "Démarrer" };
  if (done) return { kind: "status", to: "in_progress", label: "Rouvrir", needs: "reason" };
  return { kind: "contact", label: "Consigner un échange" };
}

const SYSTEM_TEXT = {
  created: (d) => (d.via === "jamora_app" ? "Ticket ouvert depuis Jamora (Contacter le support)" : "Ticket ouvert"),
  attached: (d) => `Rattaché à la pharmacie ${d.pharmacy_name || ""}`.trim(),
  updated: () => "Ticket modifié",
  assigned: (d, name) => (d.to ? `Responsable : ${name(d.to)}` : "Responsable retiré"),
  priority: (d) => (d.lifted ? `Ajustement de priorité levé (${PRIORITY_LABELS[d.to] || d.to})` : `Priorité ajustée : ${PRIORITY_LABELS[d.to] || d.to}`),
  link: (d) => `Lien ajouté : ${(LINK_KIND_LABELS[d.kind] || "élément").toLowerCase()}`,
  unlink: (d) => `Lien retiré : ${(LINK_KIND_LABELS[d.kind] || "élément").toLowerCase()}`,
  satisfaction: (d) => `Satisfaction notée : ${d.score}/5`,
};

// Objects a ticket can point to (08-3, D-40 to D-45). Each is created by its own module; the ticket only keeps the link.
export const LINK_KIND_LABELS = {
  task: "Tâche",
  mission: "Mission",
  equipment_issue: "Anomalie matérielle",
  assist: "Assistance",
  github: "Ticket GitHub",
};

export const SATISFACTION_LABELS = { 1: "Très insatisfaite", 2: "Insatisfaite", 3: "Mitigée", 4: "Satisfaite", 5: "Très satisfaite" };

// Mirrors tickets/rules.clean_github_url: an https link to github.com with at least owner/repo; the server checks it again.
export function isGithubUrl(value) {
  try {
    const url = new URL((value || "").trim());
    return url.protocol === "https:" && url.hostname === "github.com" && url.pathname.split("/").filter(Boolean).length >= 2;
  } catch {
    return false;
  }
}

// Short label of a GitHub link: « owner/repo#42 » for an issue or a pull request, the path otherwise.
export function githubLabel(value) {
  try {
    const parts = new URL(value).pathname.split("/").filter(Boolean);
    if (parts.length >= 4 && ["issues", "pull"].includes(parts[2])) return `${parts[0]}/${parts[1]}#${parts[3]}`;
    return parts.join("/");
  } catch {
    return value;
  }
}

export function linkCount(links) {
  return Object.values(links || {}).reduce((n, list) => n + (list?.length || 0), 0);
}

// Rates (0..1 or null) as « 85 % » / « — ».
export function percent(rate) {
  return rate === null || rate === undefined ? "—" : `${Math.round(rate * 100)} %`;
}
const STATUS_KIND_TEXT = {
  started: "Pris en charge",
  resumed: "Repris",
  waiting: "Mis en attente",
  resolved: "Résolu",
  closed: "Clos",
  closed_without_follow_up: "Clos sans suite",
  reopened: "Rouvert",
};

// A system entry of the thread in words. `name(id)` resolves an account id (the responsible candidates or the ticket's own names).
export function systemEntryText(entry, name = () => "un compte") {
  const data = entry?.data || {};
  if (data.event === "status") {
    const base = STATUS_KIND_TEXT[data.kind] || `Statut : ${STATUS_LABELS[data.to] || data.to}`;
    if (data.kind === "waiting") return `${base} — ${STATUS_LABELS[data.to] || ""}`.trim();
    if (data.automatic && data.kind === "closed") return "Clos automatiquement (7 jours après la résolution)";
    if (data.automatic) return `${base} automatiquement (la pharmacie a répondu)`;
    return base;
  }
  const render = SYSTEM_TEXT[data.event];
  return render ? render(data, name) : "Mise à jour";
}

export function ticketErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}

export function formatDateTime(value) {
  return formatInstant(value);
}

// A <input type="datetime-local"> value for « now » in the person's time zone, and its conversion to an ISO instant for the server.
export function localNowInput(now = new Date()) {
  return nowInput(undefined, now);
}

export function localInputToIso(value) {
  return inputToIso(value);
}

// Menu badge (like Jamora Assist's queue): open tickets nobody has taken yet, among those the person sees. Only for those who handle
// tickets; refreshed on a timer and right after any action on a ticket (`TICKETS_CHANGED_EVENT`).
export const TICKETS_CHANGED_EVENT = "tickets:changed";
export const TICKETS_BADGE_POLL_MS = 30000;
export const TICKETS_QUEUE_PARAMS = { responsible: "none", state: "open", limit: 1 };

export function canSeeTicketQueue(user) {
  return !!user?.features?.tickets && hasPermission(user, TICKET_HANDLER_KEYS);
}

export function announceTicketsChanged() {
  window.dispatchEvent(new Event(TICKETS_CHANGED_EVENT));
}
